import React from 'react';
import { motion } from 'framer-motion';
interface VerbTableProps {
  verb: string;
  tense: 'present' | 'past' | 'perfect';
  persons: string[];
  forms: string[];
  highlight?: string;
  label?: string;
}
/**
 * VerbTable - Dutch verb conjugation table visual
 *
 * Shows verb conjugation patterns with educational explanations.
 * Used for SWW (Spelling Werkwoorden) module questions.
 */
export function VerbTable({
  verb,
  tense,
  persons,
  forms,
  highlight,
  label
}: VerbTableProps) {
  // Extract stem (remove -en ending)
  const stem = verb.endsWith('en') ? verb.slice(0, -2) : verb;
  // Tense labels
  const tenseLabels = {
    present: 'Tegenwoordige Tijd',
    past: 'Verleden Tijd',
    perfect: 'Voltooid Deelwoord'
  };
  return <div className="w-full max-w-3xl mx-auto" data-id="element-6032">
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 md:p-8 border-2 border-indigo-200 shadow-lg" data-id="element-6033">
        {/* Header */}
        <div className="mb-6 text-center" data-id="element-6034">
          <div className="inline-flex items-center gap-3 bg-white/90 rounded-xl px-5 py-3 border border-indigo-200 shadow-sm" data-id="element-6035">
            <span className="text-2xl" data-id="element-6036">📝</span>
            <div className="text-left" data-id="element-6037">
              <div className="text-sm font-bold text-indigo-900" data-id="element-6038">
                {label || 'Werkwoord Vervoegen'}
              </div>
              <div className="text-xs text-indigo-600 font-semibold" data-id="element-6039">
                {tenseLabels[tense]}
              </div>
            </div>
          </div>
        </div>

        {/* Verb Info */}
        <div className="mb-6 bg-white/80 rounded-xl p-4 border border-indigo-100" data-id="element-6040">
          <div className="flex items-center justify-center gap-4 flex-wrap" data-id="element-6041">
            <div className="text-center" data-id="element-6042">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1" data-id="element-6043">
                Werkwoord
              </div>
              <div className="text-2xl font-black text-indigo-700" data-id="element-6044">{verb}</div>
            </div>
            <div className="text-2xl text-slate-300" data-id="element-6045">→</div>
            <div className="text-center" data-id="element-6046">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1" data-id="element-6047">
                Stam
              </div>
              <div className="text-2xl font-black text-purple-700" data-id="element-6048">{stem}</div>
            </div>
          </div>
        </div>

        {/* Conjugation Table */}
        <div className="bg-white/90 rounded-xl overflow-hidden border border-indigo-200 shadow-sm mb-6" data-id="element-6049">
          <table className="w-full" data-id="element-6050">
            <thead data-id="element-6051">
              <tr className="bg-indigo-100 border-b border-indigo-200" data-id="element-6052">
                <th className="px-4 py-3 text-left text-sm font-bold text-indigo-900" data-id="element-6053">
                  Persoon
                </th>
                <th className="px-4 py-3 text-left text-sm font-bold text-indigo-900" data-id="element-6054">
                  Vorm
                </th>
              </tr>
            </thead>
            <tbody data-id="element-6055">
              {persons.map((person, index) => {
              const form = forms[index];
              const isHighlighted = highlight === person || highlight === form;
              return <motion.tr key={person} initial={{
                opacity: 0,
                x: -10
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: index * 0.1
              }} className={`border-b border-slate-100 last:border-b-0 ${isHighlighted ? 'bg-indigo-50' : 'hover:bg-slate-50'}`} data-id="element-6056">

                    <td className="px-4 py-3" data-id="element-6057">
                      <span className={`font-bold ${isHighlighted ? 'text-indigo-700' : 'text-slate-700'}`} data-id="element-6058">

                        {person}
                      </span>
                    </td>
                    <td className="px-4 py-3" data-id="element-6059">
                      <span className={`font-bold text-lg ${isHighlighted ? 'text-indigo-700' : 'text-slate-800'}`} data-id="element-6060">

                        {form}
                      </span>
                    </td>
                  </motion.tr>;
            })}
            </tbody>
          </table>
        </div>

        {/* Educational Explanations */}
        <div className="space-y-4" data-id="element-6061">
          {/* How to find the stem */}
          <div className="bg-white/80 rounded-xl p-5 border border-blue-100 shadow-sm" data-id="element-6062">
            <div className="flex items-start gap-3" data-id="element-6063">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-6064">
                <span className="text-blue-600 text-lg" data-id="element-6065">🔍</span>
              </div>
              <div data-id="element-6066">
                <h4 className="text-sm font-bold text-blue-900 mb-2" data-id="element-6067">
                  Hoe vind je de stam?
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-6068">
                  De <strong data-id="element-6069">stam</strong> krijg je door <strong data-id="element-6070">-en</strong>{' '}
                  weg te halen van het hele werkwoord.{' '}
                  <span className="font-mono bg-blue-50 px-2 py-0.5 rounded border border-blue-200" data-id="element-6071">
                    {verb}
                  </span>{' '}
                  →{' '}
                  <span className="font-mono bg-purple-50 px-2 py-0.5 rounded border border-purple-200" data-id="element-6072">
                    {stem}
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Conjugation pattern */}
          {tense === 'present' && <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-6073">
              <div className="flex items-start gap-3" data-id="element-6074">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-6075">
                  <span className="text-emerald-600 text-lg" data-id="element-6076">📋</span>
                </div>
                <div data-id="element-6077">
                  <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-6078">
                    Patroon Tegenwoordige Tijd
                  </h4>
                  <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-6079">
                    <p data-id="element-6080">
                      <strong data-id="element-6081">ik:</strong> stam (geen -t) →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-6082">
                        {stem}
                      </span>
                    </p>
                    <p data-id="element-6083">
                      <strong data-id="element-6084">jij/je, u:</strong> stam + t →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-6085">
                        {stem}t
                      </span>
                    </p>
                    <p data-id="element-6086">
                      <strong data-id="element-6087">hij/zij/het:</strong> stam + t →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-6088">
                        {stem}t
                      </span>
                    </p>
                    <p data-id="element-6089">
                      <strong data-id="element-6090">wij/jullie/zij:</strong> hele werkwoord →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-6091">
                        {verb}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>}

          {/* Tip */}
          <div className="bg-amber-50/80 rounded-xl p-5 border border-amber-200 shadow-sm" data-id="element-6092">
            <div className="flex items-start gap-3" data-id="element-6093">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-6094">
                <span className="text-amber-600 text-lg" data-id="element-6095">💡</span>
              </div>
              <div data-id="element-6096">
                <h4 className="text-sm font-bold text-amber-900 mb-2" data-id="element-6097">
                  Handige tip
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-6098">
                  Bij <strong data-id="element-6099">inversie</strong> (als 'jij' achter het werkwoord
                  staat) valt de -t weg: <em data-id="element-6100">jij loopt</em> wordt{' '}
                  <em data-id="element-6101">loop jij?</em>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}